import * as React from 'react'
import * as Redux from 'react-redux'
import styled from 'styled-components'

import * as api from '@owl-nest/api-client/latest'
import * as ds from '@ulule/owl-kit-components/next'
import { UserLabel, styles as S } from '@ulule/owl-kit-components/next'
import * as model from '@owl-nest/models'
import { linkify } from '@owl-nest/utils'
import { formatDistanceToNow } from '@owl-nest/date'
import { t } from '@owl-nest/localize'

import { useAuth } from '@owl-nest/redux-wrapper/hooks'

import { RootState } from '../../../../../reducers'
import { Project } from '../../../../models/project'

type CommentProps = {
  comment: api.Comment
  onReply: () => void
  onDelete?: () => void
}

export function Comment({ comment, onReply, onDelete }: CommentProps): React.ReactElement<CommentProps> {
  const project = Redux.useSelector<RootState, Project>((state) => state.application.project.data)
  const tag = model.user.tag(comment.user, comment.user.role)
  const auth = useAuth()
  const isUserStaff = auth.type === 'loggedin' && auth.user.is_staff
  const isCommentAuthor = auth.type === 'loggedin' && auth.user.id === comment.user.id
  const canDeleteComment = isUserStaff || isCommentAuthor

  const supporterRoles = [api.UserRole.Supporter, api.UserRole.Editor, api.UserRole.Moderator, api.UserRole.Owner]
  const isUserAllowed = (project.user_role && supporterRoles.includes(project.user_role)) || isUserStaff
  const enableReply =
    project.comments_enabled === api.ProjectCommentsPermission.EVERYONE ||
    (project.comments_enabled === api.ProjectCommentsPermission.SUPPORTERS && isUserAllowed)
  
  return (
    <Wrapper>
      <UserLabel
        href={comment.user.is_public ? comment.user.absolute_url:""}
        avatar={model.user.avatar(comment.user, '90x90')}
        avatarSize="small"
        name={model.user.fullName(comment.user)}
        tag={tag}
      />

      <Body>
        <S.copy.M dangerouslySetInnerHTML={{ __html: linkify(comment.comment) }} />

        <Footer>
          <S.heading.XXXXXS as="time" dateTime={comment.submit_date}>
            {formatDistanceToNow(new Date(comment.submit_date))}
          </S.heading.XXXXXS>
          {enableReply && auth.type === 'loggedin' && <PrimaryButton onClick={onReply}>{t('Reply')}</PrimaryButton>}
          {canDeleteComment && (
            <SecondaryButton kind="secondary" uppercase onClick={onDelete}>
              {t('Delete')}
            </SecondaryButton>
          )}
        </Footer>
      </Body>
    </Wrapper>
  )
}

const PrimaryButton = styled(ds.LinkAsButton).attrs((props) => ({
  ...props,
  kind: 'primary',
  uppercase: true,
  tinted: true,
}))``

const SecondaryButton = styled(ds.LinkAsButton).attrs((props) => ({ ...props, kind: 'secondary', uppercase: true }))`
  margin-left: auto;
`

const Footer = styled.footer`
  display: flex;
  flex-direction: row;
  align-items: center;
`

const Body = styled.div`
  ${S.copy.M} {
    margin: 10px 0;
  }
`

const Wrapper = styled.article`
  display: flex;
  flex-direction: column;
  padding-bottom: 20px;

  ${Body} {
    margin-left: 47px;
  }
`
